import { OptionItem } from 'components/Formutil/SelectFormutil';
import { FieldOption } from 'types/common';
import { CustomerInfoKeyType, IdTypeEnum, NameTypeEnum, SuffixEnum } from 'types/customer';
import { RealNameInfoFormutil } from '.';

/* --------------- 配置项 --------------- */
export const suffixOptions: OptionItem[] = [
    // 经产品确认, 此处文案仅保留英文翻译
    { value: SuffixEnum.None, label: 'None' },
    { value: SuffixEnum.Jr, label: 'Jr.' },
    { value: SuffixEnum.Sr, label: 'Sr.' },
    { value: SuffixEnum.I, label: 'I' },
    { value: SuffixEnum.II, label: 'II' },
    { value: SuffixEnum.III, label: 'III' }
];

/* --------------- 各种方法 --------------- */
/**
 * @description 获取对应的姓名格式类型
 * @param idType 证件类型，用来判断是否是ssn用户
 * @param country 国籍
 */
export function getNameType(params: { idType?: IdTypeEnum; country?: string }): NameTypeEnum {
    const { idType, country } = params;
    const isSSN = idType === IdTypeEnum.SSN_IDS;

    // SSN或者非SSN其它国籍均使用USA姓名格式，非SSN中国国籍使用CHN姓名格式 https://tigertech.feishu.cn/wiki/CPzBwLSswi6kP4kGHoFczcuFnnf @huangxin
    return isSSN || country !== 'CHN' ? NameTypeEnum.USA : NameTypeEnum.CHN;
}

/**
 * @description 获取不同姓名类型下的姓名字段的配置
 * 参考 https://apidocs.tigerfintech.com/repository/1/markdown/master/docs/%E5%BC%80%E6%88%B7%E5%AD%97%E6%AE%B5%E6%9E%9A%E4%B8%BE-%E4%B8%AA%E4%BA%BA.md
 */
function getFieldsOptionByNameType(nameType: NameTypeEnum, isIncludeNameType: boolean): FieldOption[] {
    const defaultFieldType: FieldOption[] = isIncludeNameType
        ? [
              {
                  name: 'name_type',
                  required: true
              }
          ]
        : [];

    switch (nameType) {
        case NameTypeEnum.COMMON2:
            return [
                ...defaultFieldType,
                {
                    name: 'first_name',
                    required: true
                },
                {
                    name: 'last_name',
                    required: true
                }
            ];
        case NameTypeEnum.USA:
            return [
                ...defaultFieldType,
                {
                    name: 'first_name',
                    required: true
                },
                {
                    name: 'middle_initial',
                    required: false
                },
                {
                    name: 'last_name',
                    required: true
                },
                {
                    name: 'suffix',
                    required: false
                }
            ];
        case NameTypeEnum.COMMON:
            return [
                ...defaultFieldType,
                {
                    name: 'first_name',
                    required: true
                },
                {
                    name: 'middle_initial',
                    required: false
                },
                {
                    name: 'last_name',
                    required: true
                }
            ];
        case NameTypeEnum.CHN:
            return [
                ...defaultFieldType,
                {
                    name: 'real_name',
                    required: true
                },
                {
                    name: 'first_name',
                    required: true
                },
                {
                    name: 'last_name',
                    required: true
                }
            ];
        default:
            const _checkNever: never = nameType;

            return [];
    }
}

export const getFullName = (customerInfo: RealNameInfoFormutil) => {
    const { first_name, last_name, name_type, middle_initial, suffix } = customerInfo;

    switch (name_type) {
        case NameTypeEnum.COMMON2:
            return [first_name, last_name].filter(Boolean).join(' ');
        case NameTypeEnum.USA:
            return [
                first_name,
                middle_initial,
                last_name,
                // suffix是枚举值(number), 所以这里要映射成对应的label, 但是suffix是NONE的时候不往姓名后面做填充
                suffixOptions?.find(i => i.value !== SuffixEnum.None && i.value === suffix)?.label
            ]
                .filter(Boolean)
                .join(' ');
        case NameTypeEnum.COMMON:
            return [first_name, middle_initial, last_name].filter(Boolean).join(' ');
        case NameTypeEnum.CHN:
            return [last_name, first_name].filter(Boolean).join(' ');
        default:
            const _checkNever: never = name_type;

            return '';
    }
};

/**
 * @description 获取不同nameType下的默认表单字段
 * @param {Boolean} isIncludeNameType 返回的数组里面是否包含name_type本身
 */
export function getFieldsByNameType(nameType: NameTypeEnum, isIncludeNameType: boolean): CustomerInfoKeyType[] {
    return getFieldsOptionByNameType(nameType, isIncludeNameType).map(({ name }) => name);
}
